import React, { FC } from 'react'
import { graphql, usePaginationFragment } from 'react-relay'

import {
  Column,
  Grid,
  GuildCard,
  HorizontalLine,
  Stack,
  Text,
  useBreakpoint,
  useInModal,
} from '@components'

import {
  PresentationGuilds_presentation$key,
  PresentationGuilds_presentation$data,
} from './__generated__/PresentationGuilds_presentation.graphql'
import { useCardInitialCounts } from './useCardInitialCounts'

type PresentationGuildProps = {
  presentation: PresentationGuilds_presentation$key
}

const PresentationGuilds: FC<PresentationGuildProps> = (props) => {
  const { data } = usePaginationFragment(
    graphql`
      fragment PresentationGuilds_presentation on Presentation
      @refetchable(queryName: "PresentationGuildsPaginationQuery")
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 4 }
        cursor: { type: "Cursor" }
      ) {
        guilds(first: $count, after: $cursor)
          @connection(key: "PresentationGuilds_presentation_guilds") {
          edges {
            node {
              id
              ...GuildCard_guild
            }

            cursor
          }
        }
      }
    `,
    props.presentation
  )

  if (data.guilds.edges.length === 0) {
    return null
  }

  return (
    <Stack gap={5}>
      <Stack direction='horizontal' gap={4} alignItems='center'>
        <Text size={4} variant='cardHeading'>
          Presented with these Guilds
        </Text>
        <HorizontalLine variant='cardHeading' />
      </Stack>
      <Stack>
        <GuildCardGrid edges={data.guilds.edges} />
      </Stack>
    </Stack>
  )
}

const GuildCardGrid: FC<{
  edges: PresentationGuilds_presentation$data['guilds']['edges']
}> = ({ edges }) => {
  const breakpoint = useBreakpoint()
  const initialCounts = useCardInitialCounts()

  if (breakpoint === 0) {
    return (
      <Stack gap={4}>
        {edges.map((edge) => (
          <GuildCard
            guild={edge.node}
            key={edge.node.id}
            limitDescriptionLines={3}
          />
        ))}
      </Stack>
    )
  }

  const guildGroups = edges.reduce<Array<Array<(typeof edges)[number]>>>(
    (memo, guild, index) => {
      if (index % initialCounts[breakpoint] === 0) {
        memo.push([guild])
      } else {
        memo[memo.length - 1].push(guild)
      }

      return memo
    },
    []
  )

  return (
    <Stack gap={4}>
      {guildGroups.map((group, index) => {
        return (
          <Stack key={index}>
            <Grid gap={4}>
              {group.map((edge) => {
                return (
                  <Column key={edge.cursor}>
                    <GuildCard
                      expand
                      guild={edge.node}
                      limitDescriptionLines={3}
                    />
                  </Column>
                )
              })}

              {Array.from({
                length: initialCounts[breakpoint] - group.length,
              }).map((_, index) => (
                <Column key={index} />
              ))}
            </Grid>
          </Stack>
        )
      })}
    </Stack>
  )
}

export default PresentationGuilds
